import { TextField as MUITextField, useTheme, Box, Typography } from '@mui/material';
import type { TextFieldProps as MUITextFieldProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { forwardRef } from 'react';

import {
  TEXTFIELD_ERROR_ILL_FORMED_NUMBER,
  TEXTFIELD_ERROR_MAX_DIGITS_EXPONENT,
  TEXTFIELD_ERROR_MAX_DIGITS_MANTISSA,
  TEXTFIELD_ERROR_NUMERIC_MAX_VALUE,
  TEXTFIELD_ERROR_NUMERIC_MIN_VALUE,
  TEXTFIELD_ERROR_TEXT_INVALID_CHARACTERS,
  TEXTFIELD_ERROR_TEXT_MAX_LENGTH,
} from './errorCodes/TextFieldErrorCodes';
import { beep } from './helper/beep';
import { getWidthClass, getWidthStyle } from './helper/computeWidth';

/**
 * Local Props for TextField shared component
 */
type LocalTextFieldProps = {
  /**
   * @prop {string} id - id of the entry, also used are the key
   */
  id: string;

  /**
   * @prop {displayLabel} displayLabel - display label of the TextField
   */
  displayLabel: string;

  /**
   * @prop {string} value - value of the TextField
   */
  value: string;

  /**
   * @prop {boolean} readOnly
   */
  readOnly?: boolean;

  /**
   * @prop {'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'dialog' | 'auto' | number} width - size of the TextField
   * @default 'medium'
   */
  width?: 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'dialog' | 'auto' | number;

  /**
   * @prop {boolean} inError - error state of the TextField
   * @default false
   */
  error?: boolean;

  /**
   * @prop {boolean} showErrorMsg - show error message of the TextField
   * @default false
   */
  showErrorMsg?: boolean;

  /**
   * @prop {string} maxCharacterErrorMsg - error message to display if maxCharacters is exceeded
   * @default 'Max characters exceeded'
   */
  maxCharacterErrorMsg?: string;

  /**
   * @prop {string} maxCharacterReachedMsg - message to add to the character count when the max characters reached
   * @default 'Max characters reached any additional character will be ignored
   */
  maxCharactersReachedMsg?: string;

  /**
   * @prop {boolean} beepMaxCharacters - beep when maxCharacters is reached
   * @default false
   */
  beepMaxCharacters?: boolean;

  /**
   * @prop {number} maxCharacters - max size of the TextField
   */
  maxCharacters?: number;

  /**
   * @prop {boolen} displayNumCharacter - display size of the TextField
   */
  displayNumCharacter?: boolean;

  /**
   * @prop {string} characterCountLabel - label used for character count
   * @default 'characters'
   */
  characterCountLabel?: string;

  /**
   * @prop {boolean} excludeLeadTailSpace - exclude leading & tailing space of the TextField
   * @default false
   */
  excludeLeadTailSpace?: boolean;

  /**
   * @prop {boolean} allowNegative - allow negative number of the TextField, integer and float only
   * @NOTE: This is only applicable to type integer and float
   * @default false
   */
  allowNegative?: boolean;

  /**
   * @prop {number} maxDigitsNumeric - max digits of the TextField, integer and float only
   * @NOTE: This is only applicable to type integer and float.
   * @NOTE: This is only applicable to the integer part of the number.
   * @NOTE: If negative the minus sign is not counted as a digit.
   * @default 8
   */
  maxDigitsNumeric?: number;

  /**
   * @prop {number} maxDigitsExponent - max digits of the exponent of the TextField, float only
   * @NOTE: This is only applicable to type float.
   * @NOTE: The decimal point is not counted as a digit.
   * @default 3
   */
  maxDigitsExponent?: number;

  /**
   * @prop {boolean} beepMaxDigits - beep when maxDigits is reached
   * @default false
   */
  beepMaxDigits?: boolean;

  /**
   * @prop {number} minNumericValue - min value of the TextField, integer and float only
   */
  minNumericValue?: number;

  /**
   * @prop {string} minNumericValueErrorMsg - error message to display if minNumericValue is exceeded
   * @default 'Min value exceeded'
   */
  minNumericValueErrorMsg?: string;

  /**
   * @prop {number} maxNumericValue - max value of the TextField, integer and float only
   */
  maxNumericValue?: number;

  /**
   * @prop {string} maxNumericValueErrorMsg - error message to display if maxNumericValue is exceeded
   * @default 'Max value exceeded'
   */
  maxNumericValueErrorMsg?: string;

  /**
   * @prop {RegExp} validCharacters - valid characters of the TextField
   */
  validCharacters?: RegExp;

  /**
   * @prop {boolean} beepInvalidCharacters - beep when invalid characters are entered
   */
  beepInvalidCharacters?: boolean;

  /**
   * @prop {string} invalidCharacterErrorMsg - error message to display if invalid characters are entered
   * @default 'Invalid character entered'
   */
  invalidCharacterErrorMsg?: string;

  /**
   * @prop {string} helperText - helper text of the TextField
   */
  helperText?: string;

  /**
   * @prop {callback} onChange - change handler
   * @param {string} id - id of the TextField
   * @param {string} value - value of the TextField
   * @param {boolean} inError - if the value is valid
   * @param {string} errorCode - error code of the TextField
   * @param {string} trimValue - if excludeLeadTailSpace is true, this is the value without leading & tailing spaces
   */
  onChange?: (
    id: string,
    value: string,
    inError: boolean,
    errorCode: string,
    trimValue: string
  ) => void;

  /**
   * @prop {callback} onKeyUp - each character key up handler
   * @param {string} id - id of the TextField
   * @param {string} key - key just typed of the TextField
   */
  onKeyUp?: (id: string, key: string) => void;

  /**
   * @prop {callback} onFocus - focus handler
   * @param {string} id - id of the TextField
   */
  onFocus?: (id: string) => void;

  /**
   * @prop {callback} onBlur - blur handler
   * @param {string} id - id of the TextField
   * @param {string} value - value of the TextField
   * @param {boolean} inError - if the value in eerror
   * @param {string} errorCode - error code of the TextField
   * @param {string} trimValue - if excludeLeadTailSpace is true, this is the value without leading & tailing spaces
   */
  onBlur?: (
    id: string,
    value: string,
    inError: boolean,
    errorCode: string,
    trimValue: string
  ) => void;
};

/**
 * Override props for MUI TextField - this allow the app's to control the behavior of the Button as needed
 * When overriding a prop, we should also omit the prop from the MuiButtonProps selection
 * to ensure that the prop def when viewed only shows our override
 */
type MUIOverriddenTextFieldProps = {
  /**
   * @prop {'outlined'} variant - variant of the TextField at this time only outlined is supported
   * @default 'outlined'
   */
  //variant?: 'outlined';

  /**
   * @prop {'password' | 'text' | 'number' | 'email' | 'integer' | 'float'} type - type of the TextField at this time
   * @default 'text'
   */
  type?: 'password' | 'text' | 'number' | 'email' | 'integer' | 'float';

  /**
   * @prop {boolean} viewMode - To display the TextField in the view mode
   * @default false
   * */
  viewMode?: boolean;
};

export type TextFieldProps = LocalTextFieldProps &
  MUIOverriddenTextFieldProps &
  Omit<
    MUITextFieldProps,
    | 'helperText'
    | 'label'
    | 'error'
    | 'color'
    | 'variant'
    | 'type'
    | 'onChange'
    | 'onKeyUp'
    | 'onFocus'
    | 'onBlur'
    | 'fullWidth'
    | 'value'
    | 'variant'
  >;

const CustomTextField = styled(MUITextField)((theme) => ({
  '& .MuiFormControl-root-MuiTextField-root .MuiOutlinedInput-root fieldset': {
    borderWidth: '1px',
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    color: '#252A36',
    '& fieldset': {
      borderColor: '#d3d4d7',
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderColor: '#000',
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
    },
  },
  '& .MuiTextField-root .input::placeholder': {
    opacity: 1,
    color: 'red',
    WebkitTextFillColor: '#666666',
  },

  '&. MuiInputLabel-root': {
    color: '#666666',
  },
  '& .MuiInputLabel-root.Mui-disabled': {
    color: '#d2d4d7',
    WebkitTextFillColor: '#d2d4d7',
  },
  '& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
    color: '#d2d4d7',
    WebkitTextFillColor: '#d2d4d7',
    fontStyle: 'italic',
  },
  '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderWidth: '1px',
  },
}));

const TextFieldContainer = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  '&.--width-xxlarge': {
    width: '684px',
  },
  '&.--width-xlarge': {
    width: '556px',
  },
  '&.--width-large': {
    width: '448px',
  },
  '&.--width-medium': {
    width: '330px',
  },
  '&.--width-small': {
    width: '212px',
  },
  '&.--width-dialog': {
    width: '400px',
  },
  '&.--width-auto': {
    width: '100%',
  },
}));

const TextFieldViewMode = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'Roboto, sans-serif',
}));

const TextFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#666',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const TextFieldValue = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#252a36',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const {
    id,
    width = 'large',
    type = 'text',
    displayLabel,
    value,
    placeholder,
    defaultValue,
    disabled,
    required,
    readOnly,
    displayNumCharacter,
    characterCountLabel = 'characters',
    excludeLeadTailSpace,
    maxCharacters,
    maxCharacterErrorMsg = 'Max characters exceeded',
    maxCharactersReachedMsg = 'Max characters reached any additional character will be ignored',
    beepMaxCharacters = false,
    allowNegative = false,
    maxDigitsNumeric = 8,
    maxDigitsExponent = 3,
    beepMaxDigits = false,
    minNumericValue,
    minNumericValueErrorMsg = 'Min value exceeded',
    maxNumericValue,
    maxNumericValueErrorMsg = 'Max value exceeded',
    helperText,
    size = 'medium',
    autoComplete = 'off',
    inputProps,
    showErrorMsg = false, // display as error exceeded maxCharacters and invalid characters
    validCharacters,
    beepInvalidCharacters = false,
    invalidCharacterErrorMsg = 'Invalid characters entered',
    error,
    viewMode = false,
    onFocus,
    onBlur,
    onChange,
    onKeyUp,
    onKeyDown,
    sx,
    ...rest
  } = props;

  const theme = useTheme();

  const allowedExtraCharacters = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'Shift',
    'Control',
    'Alt',
    'Meta',
    'Escape',
    'Home',
    'End',
    'PageUp',
    'PageDown',
  ];

  const inError = (val: string): { inError: boolean; errorCode: string } => {
    if (type === 'integer' || type === 'float') {
      if (val === '-' || val === '.' || val === '-.') {
        return { inError: true, errorCode: TEXTFIELD_ERROR_ILL_FORMED_NUMBER };
      } else {
        return { inError: false, errorCode: '' };
      }
    }

    const v = excludeLeadTailSpace ? val.trim() : val;

    // see if the current value exceeds the maxCharacters
    if (maxCharacters && v.length > maxCharacters) {
      return { inError: true, errorCode: TEXTFIELD_ERROR_TEXT_MAX_LENGTH };
    }

    // see if there are any invalid characters on the value
    let error = false;
    if (validCharacters && val) {
      val.split('').some((c) => {
        if (!validCharacters.test(c)) {
          error = true;
          return true;
        } else return false;
      });
    }

    if (error) {
      return { inError: true, errorCode: TEXTFIELD_ERROR_TEXT_INVALID_CHARACTERS };
    }

    return { inError: false, errorCode: '' };
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'integer' || type === 'float') {
      let errorCode = '';

      // handle type integer and float
      const inEvent = e.target;
      let val = e.target.value;
      const [mantissa, exponent] = val.split('.');
      // there should always be a mantissa so check it length, make sure to not count the - if negative
      const mantissaLen = mantissa[0] === '-' ? mantissa.length - 1 : mantissa.length;
      if (mantissaLen > maxDigitsNumeric && !allowedExtraCharacters.includes(val)) {
        // set the value to the previous value
        val = value;

        const cursorLoc = inEvent.selectionStart ? inEvent.selectionStart - 1 : 0;
        // set the cursor to the previous position
        setTimeout(() => {
          inEvent.selectionStart = cursorLoc;
          inEvent.selectionEnd = cursorLoc;
        });

        if (beepMaxDigits) beep();

        // set the error code
        errorCode = TEXTFIELD_ERROR_MAX_DIGITS_MANTISSA;
      } else if (
        exponent &&
        exponent.length > maxDigitsExponent &&
        !allowedExtraCharacters.includes(val)
      ) {
        // set the value to the previous value
        val = value;
        const cursorLoc = inEvent.selectionStart ? inEvent.selectionStart - 1 : 0;
        // set the cursor to the previous position
        setTimeout(() => {
          inEvent.selectionStart = cursorLoc;
          inEvent.selectionEnd = cursorLoc;
        }, 0);

        if (beepMaxDigits) beep();

        // set the error code
        errorCode = TEXTFIELD_ERROR_MAX_DIGITS_EXPONENT;
      }

      if (errorCode !== '') {
        onChange && onChange(id, val, true, errorCode, val);
      } else {
        const error = inError(e.currentTarget.value);
        onChange && onChange(id, val, error.inError, error.errorCode, val);
      }
    } else if (maxCharacters && !showErrorMsg) {
      // handle type text
      if (excludeLeadTailSpace) {
        const v = e.currentTarget.value.trim();
        e.currentTarget.value = e.currentTarget.value.substring(
          0,
          maxCharacters + (e.currentTarget.value.length - v.length)
        );
      } else {
        e.currentTarget.value = e.currentTarget.value.substring(0, maxCharacters);
      }

      const error = inError(e.currentTarget.value);
      onChange &&
        onChange(
          id,
          e.target.value,
          error.inError,
          error.errorCode,
          excludeLeadTailSpace ? e.currentTarget.value.trim() : e.currentTarget.value
        );
    } else {
      const error = inError(e.currentTarget.value);
      onChange &&
        onChange(
          id,
          e.target.value,
          error.inError,
          error.errorCode,
          excludeLeadTailSpace ? e.currentTarget.value.trim() : e.currentTarget.value
        );
    }
  };

  const onBlurHandier = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'integer' || type === 'float') {
      if (e.target.value === '-' || e.target.value === '.' || e.target.value === '-.') {
        onBlur &&
          onBlur(id, e.target.value, true, TEXTFIELD_ERROR_ILL_FORMED_NUMBER, e.target.value);
      } else if (type === 'integer') {
        if (maxNumericValue && parseInt(e.target.value, 10) > maxNumericValue) {
          onBlur &&
            onBlur(id, e.target.value, true, TEXTFIELD_ERROR_NUMERIC_MAX_VALUE, e.target.value);
        } else if (minNumericValue && parseInt(e.target.value, 10) < minNumericValue) {
          onBlur &&
            onBlur(id, e.target.value, true, TEXTFIELD_ERROR_NUMERIC_MIN_VALUE, e.target.value);
        } else {
          onBlur && onBlur(id, e.target.value, false, '', e.target.value);
        }
      } else if (type === 'float') {
        if (maxNumericValue && parseFloat(e.target.value) > maxNumericValue) {
          onBlur &&
            onBlur(id, e.target.value, true, TEXTFIELD_ERROR_NUMERIC_MAX_VALUE, e.target.value);
        } else if (minNumericValue && parseFloat(e.target.value) < minNumericValue) {
          onBlur &&
            onBlur(id, e.target.value, true, TEXTFIELD_ERROR_NUMERIC_MIN_VALUE, e.target.value);
        } else {
          onBlur && onBlur(id, e.target.value, false, '', e.target.value);
        }
      }
    } else {
      const error = inError(e.currentTarget.value);
      onBlur &&
        onBlur(
          id,
          e.target.value,
          error.inError,
          error.errorCode,
          excludeLeadTailSpace ? e.currentTarget.value.trim() : e.currentTarget.value
        );
    }
  };

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // if the character just typed is invalid don't display it
    const target = e.target as HTMLInputElement;

    // Allow navigation with Cmd (Mac) or Ctrl (Windows/Linux)
    if (e.metaKey || e.ctrlKey) {
      return;
    }

    if (type === 'integer') {
      const reg = allowNegative ? /^-?\d*$/ : /^\d*$/;
      if (!reg.test(e.key) && !allowedExtraCharacters.includes(e.key)) {
        e.preventDefault();
      }

      // make sure there is a single - at the beginning of the value
      if (e.key === '-' && target.selectionStart !== 0) {
        e.preventDefault();
      } else if (e.key === '-' && target.selectionStart === 0 && value[0] === '-') {
        e.preventDefault();
      }
    } else if (type === 'float') {
      // check for allowed characters
      const reg = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
      if (!reg.test(e.key) && !allowedExtraCharacters.includes(e.key)) {
        e.preventDefault();
      }

      // make sure there is only a single decimal point
      if (e.key === '.' && value.includes('.')) {
        e.preventDefault();
      }

      // make sure there is a single - at the beginning of the value
      if (e.key === '-' && target.selectionStart !== 0) {
        e.preventDefault();
      } else if (e.key === '-' && target.selectionStart === 0 && value[0] === '-') {
        e.preventDefault();
      }
    } else {
      if (validCharacters && !showErrorMsg) {
        if (!validCharacters.test(e.key)) {
          if (beepInvalidCharacters && !allowedExtraCharacters.includes(e.key)) {
            beep();
          }
          e.preventDefault();
        }
      }

      // see if beep need to extra character limit what is displayed
      if (
        beepMaxCharacters &&
        maxCharacters &&
        val.length >= maxCharacters &&
        !allowedExtraCharacters.includes(e.key)
      ) {
        beep();

        return;
      }

      // trim the leading space and if the tailing spaces exceed the maxCharacters don't display the character and beep
      if (
        beepMaxCharacters &&
        maxCharacters &&
        value.trimStart().length >= maxCharacters &&
        !allowedExtraCharacters.includes(e.key)
      ) {
        beep();
        e.preventDefault();
      }
    }
    onKeyDown && onKeyDown(e);
  };

  // compute the number of characters
  let numCharMsg = '';
  const val = excludeLeadTailSpace ? value.trim() : value;
  if (maxCharacters !== undefined && displayNumCharacter)
    numCharMsg = `${val.length}/${maxCharacters} ${characterCountLabel}`;
  else if (!maxCharacters && displayNumCharacter)
    numCharMsg = `${value !== undefined ? value.length : 0} ${characterCountLabel}`;

  // add the maxCharactersReachedMsg if the maxCharacters is reached
  if (maxCharactersReachedMsg && maxCharacters && val.length > maxCharacters) {
    if (displayNumCharacter) numCharMsg += ' - ';
    numCharMsg += `${maxCharactersReachedMsg}`;
  }

  // see if there is a maxCharacters error and showError is true
  let inputError = false;
  let inputErrorMsg = '';
  if (maxCharacters && showErrorMsg) {
    const v = excludeLeadTailSpace ? value.trim() : value;
    inputError = v.length > maxCharacters;
    inputErrorMsg =
      numCharMsg + (maxCharacterErrorMsg && inputError ? ` - ${maxCharacterErrorMsg}` : '');
  }

  // // see if there is a numeric error min or max value
  if ((type === 'integer' || type === 'float') && showErrorMsg) {
    if (type === 'float' && maxNumericValue && parseFloat(value) > maxNumericValue) {
      inputError = true;
      inputErrorMsg = maxNumericValueErrorMsg;
    } else if (type === 'float' && minNumericValue && parseFloat(value) < minNumericValue) {
      inputError = true;
      inputErrorMsg = minNumericValueErrorMsg;
    } else if (type === 'integer' && maxNumericValue && parseFloat(value) > maxNumericValue) {
      inputError = true;
      inputErrorMsg = maxNumericValueErrorMsg;
    } else if (type === 'integer' && minNumericValue && parseFloat(value) < minNumericValue) {
      inputError = true;
      inputErrorMsg = minNumericValueErrorMsg;
    } else {
      inputError = false;
      inputErrorMsg = '';
    }
  }

  // test to see if there is an invalid character error and showError is true
  if (showErrorMsg && validCharacters && value) {
    value.split('').some((c) => {
      if (!validCharacters.test(c)) {
        inputError = true;
        inputErrorMsg = invalidCharacterErrorMsg;

        return true;
      } else return false;
    });
  }

  const thereIsAnError = error || inputError;
  const computedType = type === 'integer' || type === 'float' ? 'text' : type;

  return (
    <TextFieldContainer
      data-testid={`textField-${id}-container`}
      className={getWidthClass(width)}
      style={getWidthStyle(width)}
    >
      {viewMode ? (
        <TextFieldViewMode
          id={`textField-${id}-container-viewMode`}
          data-testid={`text-field-view-mode-${id}`}
        >
          <TextFieldLabel>{displayLabel}</TextFieldLabel>
          <TextFieldValue>{value}</TextFieldValue>
        </TextFieldViewMode>
      ) : (
        <CustomTextField
          {...rest}
          ref={ref}
          id={`textField-${id}`}
          data-testid={`textField-${id}`}
          value={value}
          type={computedType}
          autoComplete={autoComplete}
          defaultValue={defaultValue ? defaultValue : undefined}
          label={displayLabel}
          placeholder={placeholder}
          helperText={error && helperText ? helperText : inputErrorMsg || numCharMsg || helperText}
          error={error}
          disabled={disabled}
          required={required}
          variant={'outlined'}
          InputProps={{
            readOnly: readOnly,
            'aria-readonly': readOnly,
          }}
          InputLabelProps={{ shrink: true, sx: { color: '#333333' } }}
          size={size}
          fullWidth
          onFocus={() => onFocus && onFocus(id)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlurHandier(e)}
          onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => onKeyUp && onKeyUp(id, e.key)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(e)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => onKeyDownHandler(e)}
          sx={{
            input: {
              color: '#333333',
              WebkitTextFillColor: '#333333',
              '&::placeholder': {
                opacity: 1,
                color: '#666666',
                WebkitTextFillColor: '#ded4d7',
              },
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: thereIsAnError ? '#f14c2e' : 'rgba(0, 0, 0, 0.23)',
                borderWidth: '1px',
              },

              '&.Mui-focused fieldset': {
                borderColor: thereIsAnError ? '#f14c2e' : theme.palette.primary.main,
                borderWidth: '2px',
              },
            },
            '& .MuiInputLabel-root': {
              color: thereIsAnError ? '#f14c2e' : '#333333',

              '&.Mui-focused': {
                color: thereIsAnError ? '#f14c2e' : '#333333',
              },
            },
            '& .MuiFormHelperText-root': {
              color: thereIsAnError ? '#f14c2e' : `${theme.palette.text.secondary}`,
              marginLeft: '4px',
              marginRight: '4px',
            },
            ...sx,
          }}
        />
      )}
    </TextFieldContainer>
  );
});

export default TextField;
